import React, { useEffect } from 'react';
import { useLocation, Navigate, Link } from 'react-router-dom';
import LoginForm from '../components/auth/login-form';
import { useAuth } from '../hooks/use-auth';

interface LocationState {
  from?: {
    pathname: string;
  };
  message?: string;
}

const Login: React.FC = () => {
  const { session, isLoading } = useAuth();
  const location = useLocation();
  const state = location.state as LocationState | undefined;
  const message = state?.message;
  const from = state?.from?.pathname || '/';
  
  // Add debugging
  useEffect(() => {
    console.log('Login component state:', { 
      isAuthenticated: !!session, 
      isLoading, 
      hasSession: !!session,
      locationState: state,
      redirectTo: from 
    });
  }, [isLoading, session, state, from]);
  
  // If already authenticated, redirect to home or original page
  if (session) {
    console.log(`User is authenticated, redirecting to: ${from}`);
    return <Navigate to={from} replace />;
  }

  return (
    <div className="min-h-screen flex bg-white sm:bg-gray-50">
      {/* Left Column - Visual/Brand side (Hidden on smaller screens) */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between overflow-hidden bg-[#3D7A74]">
        {/* Abstract background elements */}
        <div className="absolute inset-0 z-0 opacity-30">
          <div className="absolute -top-[20%] -left-[10%] w-[70%] h-[70%] rounded-full bg-white blur-3xl blend-lighten"></div>
          <div className="absolute -bottom-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-[#1a4a45] blur-3xl blend-multiply"></div>
          <div className="absolute top-[40%] right-[10%] w-[40%] h-[40%] rounded-full bg-[#6db3ac] blur-3xl blend-screen opacity-50"></div>
        </div>

        <div className="relative z-10 p-12 lg:p-16 xl:p-24 flex-1 flex flex-col justify-between">
          <div>
            <Link to="/">
              <img
                className="h-20 w-auto brightness-0 invert"
                src="/logo_nobackground.png"
                alt="KAIMED Logo"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/logo.png';
                  (e.target as HTMLImageElement).classList.remove('brightness-0', 'invert');
                }}
              />
            </Link>
          </div>
          
          <div className="mt-auto pb-8">
            <h1 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
              Patient First.<br/>
              <span className="text-[#A5C9C5] font-light">Every Test Connected.</span>
            </h1>
            <p className="text-lg text-[#E0EFEF] max-w-md font-light leading-relaxed">
              Your comprehensive health portal. Access your medical records, review test results, and securely connect with your healthcare providers.
            </p>
          </div>
        </div>
      </div>

      {/* Right Column - Login Form side */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:w-1/2 lg:px-20 xl:px-32 bg-white">
        <div className="mx-auto w-full max-w-sm lg:max-w-md">
          <div className="mb-10 lg:hidden text-center sm:text-left">
            <img
              className="h-20 w-auto mb-8 mx-auto sm:mx-0"
              src="/logo.png"
              alt="KAIMED"
            />
            <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">Sign In</h2>
            <p className="mt-2 text-sm text-gray-500">
              Welcome back to your healthcare portal
            </p>
          </div>
          
          <div className="hidden lg:block mb-10">
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Sign in to your account</h2>
            <p className="mt-2 text-base text-gray-500">
              Welcome back to KaiMed
            </p>
          </div>

          {message && (
            <div className="mb-6 p-4 rounded-xl bg-green-50/80 border border-green-200 text-green-700 text-sm font-medium shadow-sm backdrop-blur-sm">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {message}
              </div>
            </div>
          )}

          <div className="bg-white sm:shadow-lg lg:shadow-none rounded-2xl sm:p-8 lg:p-0 sm:border border-gray-100 lg:border-none relative">
            {/* Subtle glow effect behind form on tablet */}
            <div className="hidden sm:block lg:hidden absolute -inset-0.5 bg-gradient-to-br from-[#3D7A74]/10 to-transparent rounded-2xl blur-lg -z-10"></div>
            
            <LoginForm redirectUrl={from} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;